<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: [Number, String],
    default: 0,
  },
  recurrenceType: {
    type: String,
    default: null,
  },
  nextRunAt: {
    type: String,
    default: null,
  },
  // eslint-disable-next-line vue/no-unused-properties
  runCount: {
    type: Number,
    default: 0,
  },
  reportRoute: {
    type: Object,
    default: null,
  },
  flowRoute: {
    type: Object,
    default: null,
  },
  // Contagem por status dos CampaignMessages (queued/sending/sent/delivered/
  // read/failed/skipped) — alimenta a barra de progresso na lista.
  messageCounts: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['edit', 'delete', 'duplicate']);

const { t } = useI18n();

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType
    ? props.isEnabled
    : !['completed', 'error'].includes(props.status)
);

const statusTextColor = computed(() => {
  if (props.status === 'error') return { 'text-n-ruby-11': true };
  if (props.status === 'running') return { 'text-n-blue-11': true };
  if (props.status === 'paused') return { 'text-n-amber-11': true };
  if (props.status === 'draft') return { 'text-n-slate-10': true };
  if (props.status === 'completed') return { 'text-n-slate-12': true };
  return {
    'text-n-teal-11': isActive.value,
    'text-n-slate-12': !isActive.value,
  };
});

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  switch (props.status) {
    case 'completed':
      return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
    case 'running':
      return t('CAMPAIGN.SMS.CARD.STATUS.RUNNING');
    case 'error':
      return t('CAMPAIGN.SMS.CARD.STATUS.ERROR');
    case 'paused':
      return t('CAMPAIGN.SMS.CARD.STATUS.PAUSED');
    case 'draft':
      return t('CAMPAIGN.SMS.CARD.STATUS.DRAFT');
    default:
      return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
  }
});

const recurrenceLabel = computed(() => {
  if (!props.recurrenceType) return null;
  const key = props.recurrenceType.toUpperCase();
  return t(`CAMPAIGN.CARD.${key}`);
});

const formattedNextRun = computed(() => {
  if (!props.nextRunAt) return null;
  const d = new Date(props.nextRunAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});

// Progresso do disparo: mostrado enquanto roda/pausada (e o resultado final
// na completada). "Feito" = tudo que já saiu da fila (enviado/entregue/lido/
// falha/pulado); a barra colore a fração de sucesso vs falha.
const progress = computed(() => {
  const counts = props.messageCounts || {};
  const total = Object.values(counts).reduce((sum, n) => sum + (n || 0), 0);
  if (!total) return null;

  const pendingCount =
    (counts.pending || 0) + (counts.queued || 0) + (counts.sending || 0);
  const failedCount = counts.failed || 0;
  const skippedCount = counts.skipped || 0;
  const okCount = total - pendingCount - failedCount - skippedCount;
  return {
    total,
    queued: pendingCount,
    failed: failedCount,
    ok: okCount,
    okPercent: Math.round((okCount / total) * 100),
    failedPercent: Math.round((failedCount / total) * 100),
  };
});

const showProgress = computed(
  () =>
    !props.isLiveChatType &&
    progress.value &&
    ['running', 'paused', 'completed'].includes(props.status)
);

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type, additional_attributes } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', additional_attributes);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span class="text-base font-medium text-n-slate-12 line-clamp-1">
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
        <span
          v-if="recurrenceLabel"
          class="text-xs font-medium inline-flex items-center gap-1 h-6 px-2 py-0.5 rounded-md bg-n-blue-3 text-n-blue-11"
        >
          <span class="i-lucide-repeat w-3 h-3" />
          {{ recurrenceLabel }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div v-if="showProgress" class="w-full flex items-center gap-3">
        <div
          class="flex-1 h-1.5 rounded-full bg-n-alpha-2 overflow-hidden flex"
        >
          <div
            class="h-full bg-n-teal-9 transition-all"
            :style="{ width: `${progress.okPercent}%` }"
          />
          <div
            class="h-full bg-n-ruby-9 transition-all"
            :style="{ width: `${progress.failedPercent}%` }"
          />
        </div>
        <span class="text-xs text-n-slate-11 whitespace-nowrap">
          {{
            t('CAMPAIGN.CARD.PROGRESS', {
              ok: progress.ok,
              total: progress.total,
            })
          }}
          <template v-if="progress.failed">
            ·
            {{ t('CAMPAIGN.CARD.PROGRESS_FAILED', { count: progress.failed }) }}
          </template>
          <template v-if="progress.queued && status !== 'completed'">
            ·
            {{ t('CAMPAIGN.CARD.PROGRESS_QUEUED', { count: progress.queued }) }}
          </template>
        </span>
      </div>
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
        <span
          v-if="formattedNextRun"
          class="text-xs text-n-slate-10 flex items-center gap-1 ml-auto"
        >
          <span class="i-lucide-calendar-clock w-3 h-3" />
          {{ t('CAMPAIGN.CARD.NEXT_RUN', { date: formattedNextRun }) }}
        </span>
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <router-link
        v-if="flowRoute"
        v-slot="{ navigate }"
        :to="flowRoute"
        custom
      >
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-git-branch"
          :title="t('CAMPAIGN.CARD.FLOW')"
          @click="navigate"
        />
      </router-link>
      <router-link
        v-if="reportRoute"
        v-slot="{ navigate }"
        :to="reportRoute"
        custom
      >
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-bar-chart-2"
          :title="t('CAMPAIGN.CARD.REPORT')"
          @click="navigate"
        />
      </router-link>
      <Button
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-pencil"
        :title="t('CAMPAIGN.CARD.EDIT')"
        @click="emit('edit')"
      />
      <Button
        v-if="!isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-copy"
        :title="t('CAMPAIGN.CARD.DUPLICATE')"
        @click="emit('duplicate')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        :title="t('CAMPAIGN.CARD.DELETE')"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
